import type { Node as PmNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { detectCueMarker, type CgTemplateKey, type CueKind } from '@airpost/shared';

/**
 * The storyboard in the script: every heading that calls for a cue (ΒΙΝΤΕΟ ΜΕ ΗΧΟ, ΤΙΤΛΟΣ 1, ΦΑΣΑ,
 * ΡΕΠΟΡΤΑΖ…) gets a slot right under it, a widget decoration that is not part of the saved text. An
 * empty slot offers to choose a clip, reserve a virtual code or add a CG title; a filled one shows the
 * cue with preview, edit and remove. The slot raises STORYBOARD_EVENT on the window; the editor page
 * acts and hands the cues back with setStoryboardCues().
 */

export interface CueSlot {
  /** `ΒΙΝΤΕΟ ΜΕ ΗΧΟ#1`: the normalized heading and its occurrence, the cue's anchor. */
  key: string;
  kind: CueKind;
  template: CgTemplateKey | null;
  label: string;
  /** Document position right after the heading block, where the slot sits. */
  pos: number;
  /** The text blocks that follow the heading (a title's line, a credit's name), for prefilling a CG cue. */
  following: string[];
}

/** What a slot shows for a cue; the page hands these in, the plugin never fetches. */
export interface SlotCue {
  id: string;
  kind: CueKind;
  anchor: string | null;
  mediaAssetId: string | null;
  mediaCode: string | null;
  template: string | null;
  lines: string[];
  durationMs: number | null;
  media: { title: string; durationMs: number; status: string } | null;
}

export type SlotActionKind = 'choose' | 'virtual' | 'cg' | 'preview' | 'edit' | 'remove';
export interface SlotAction {
  action: SlotActionKind;
  slot: CueSlot;
  cue: SlotCue | null;
}

export interface SlotLabels {
  choose: string;
  virtual: string;
  addCg: string;
  preview: string;
  edit: string;
  remove: string;
  waiting: string;
  templateName: (key: string | null) => string;
}

interface SbState {
  slots: CueSlot[];
  cues: SlotCue[];
  labels: SlotLabels;
  readOnly: boolean;
  decorations: DecorationSet;
}

export const storyboardKey = new PluginKey<SbState>('airpost-storyboard');
export const STORYBOARD_EVENT = 'airpost:storyboard-slot';

const SEPARATOR = /^[-—_=]{5,}$/;

/** Scan the top-level blocks of the script for cue headings. */
export function scanSlots(doc: PmNode): CueSlot[] {
  const blocks: Array<{ text: string; end: number }> = [];
  doc.forEach((node, offset) => {
    if (node.isBlock) blocks.push({ text: node.textContent, end: offset + node.nodeSize });
  });
  const counts = new Map<string, number>();
  const slots: CueSlot[] = [];
  blocks.forEach((b, i) => {
    const m = detectCueMarker(b.text);
    if (!m) return;
    const n = (counts.get(m.label) ?? 0) + 1;
    counts.set(m.label, n);
    const following: string[] = [];
    for (let j = i + 1; j < blocks.length && following.length < 4; j++) {
      const text = blocks[j]!.text.trim();
      if (!text) {
        if (following.length) break;
        continue;
      }
      if (detectCueMarker(text) || SEPARATOR.test(text)) break;
      following.push(text);
    }
    slots.push({ key: `${m.label}#${n}`, kind: m.kind, template: m.template, label: m.label, pos: b.end, following });
  });
  return slots;
}

const hhmmss = (ms: number) => {
  const s = Math.round(ms / 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
};

function span(cls: string, text: string, title?: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = cls;
  el.textContent = text;
  if (title) el.title = title;
  return el;
}

function slotDom(slot: CueSlot, cue: SlotCue | null, labels: SlotLabels, readOnly: boolean): HTMLElement {
  const el = document.createElement('div');
  el.className = `cue-slot ${slot.kind}${cue ? ' set' : ''}`;
  el.contentEditable = 'false';
  el.dataset.slot = slot.key;
  const fire = (action: SlotActionKind) => window.dispatchEvent(new CustomEvent<SlotAction>(STORYBOARD_EVENT, { detail: { action, slot, cue } }));
  const btn = (text: string, action: SlotActionKind, cls = 'btn small', title?: string) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.textContent = text;
    if (title) b.title = title;
    b.disabled = readOnly && action !== 'preview';
    b.addEventListener('mousedown', (e) => e.preventDefault());
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      fire(action);
    });
    return b;
  };
  el.appendChild(span('cue-icon', slot.kind === 'video' ? '🎬' : '🅒'));
  if (!cue) {
    if (slot.kind === 'video') {
      el.append(btn(`＋ ${labels.choose}`, 'choose'), btn(`⌛ ${labels.virtual}`, 'virtual'));
    } else {
      el.append(span('cue-hint', labels.templateName(slot.template)), btn(`＋ ${labels.addCg}`, 'cg'));
    }
    return el;
  }
  if (cue.kind === 'video') {
    if (cue.mediaAssetId) {
      const img = document.createElement('img');
      img.className = 'cue-thumb';
      img.alt = '';
      img.loading = 'lazy';
      img.src = `/api/media/${encodeURIComponent(cue.mediaAssetId)}/thumb`;
      img.addEventListener('error', () => img.remove());
      el.appendChild(img);
      el.appendChild(span('cue-title', cue.media?.title ?? cue.mediaAssetId, cue.mediaAssetId));
      if (cue.media) {
        el.appendChild(span('cue-dur', hhmmss(cue.media.durationMs)));
        el.appendChild(span(`status ${cue.media.status === 'READY' ? 'ok' : 'warn'}`, cue.media.status));
      }
      if (cue.mediaCode) el.appendChild(span('cue-hint', `(${cue.mediaCode})`));
      el.appendChild(span('spacer', ''));
      el.append(btn(`▶ ${labels.preview}`, 'preview'), btn('✎', 'choose', 'btn small', labels.choose), btn('✖', 'remove', 'btn small danger', labels.remove));
    } else if (cue.mediaCode) {
      el.appendChild(span('cue-code', `⌛ ${cue.mediaCode}`));
      el.appendChild(span('cue-hint', labels.waiting));
      el.appendChild(span('spacer', ''));
      el.append(btn(labels.choose, 'choose'), btn('✖', 'remove', 'btn small danger', labels.remove));
    } else {
      el.append(btn(`＋ ${labels.choose}`, 'choose'), btn(`⌛ ${labels.virtual}`, 'virtual'), span('spacer', ''), btn('✖', 'remove', 'btn small danger', labels.remove));
    }
    return el;
  }
  el.appendChild(span('cue-cg', labels.templateName(cue.template)));
  el.appendChild(span('cue-lines', cue.lines.filter(Boolean).join(' / ') || '…'));
  if (cue.durationMs) el.appendChild(span('cue-dur', `${Math.round(cue.durationMs / 1000)}″`));
  el.appendChild(span('spacer', ''));
  el.append(btn(`✎ ${labels.edit}`, 'edit'), btn('✖', 'remove', 'btn small danger', labels.remove));
  return el;
}

function build(doc: PmNode, slots: CueSlot[], cues: SlotCue[], labels: SlotLabels, readOnly: boolean): DecorationSet {
  const byAnchor = new Map<string, SlotCue>();
  for (const c of cues) if (c.anchor && !byAnchor.has(c.anchor)) byAnchor.set(c.anchor, c);
  return DecorationSet.create(
    doc,
    slots.map((s) => {
      const cue = byAnchor.get(s.key) ?? null;
      // the key decides when ProseMirror may keep the old DOM: anything the slot shows or hands to its click handlers is in it
      const key = [s.key, s.following.join('\u0002'), cue?.id ?? '', cue?.mediaAssetId ?? '', cue?.mediaCode ?? '', cue?.template ?? '', (cue?.lines ?? []).join('|'), cue?.durationMs ?? '', cue?.media?.status ?? '', readOnly ? 'ro' : 'rw'].join('\u0001');
      return Decoration.widget(s.pos, () => slotDom(s, cue, labels, readOnly), { side: -1, key, ignoreSelection: true, stopEvent: () => true });
    }),
  );
}

export function storyboardPlugin(labels: SlotLabels, readOnly: boolean): Plugin<SbState> {
  return new Plugin<SbState>({
    key: storyboardKey,
    state: {
      init: (_, state) => {
        const slots = scanSlots(state.doc);
        return { slots, cues: [], labels, readOnly, decorations: build(state.doc, slots, [], labels, readOnly) };
      },
      apply(tr: Transaction, prev: SbState): SbState {
        const meta = tr.getMeta(storyboardKey) as Partial<Pick<SbState, 'cues' | 'readOnly'>> | undefined;
        if (!meta && !tr.docChanged) return prev;
        const next = { ...prev, ...(meta ?? {}) };
        const slots = tr.docChanged ? scanSlots(tr.doc) : prev.slots;
        return { ...next, slots, decorations: build(tr.doc, slots, next.cues, next.labels, next.readOnly) };
      },
    },
    props: {
      decorations(state) {
        return storyboardKey.getState(state)?.decorations ?? null;
      },
    },
  });
}

/** Hand the plugin the cues to show (and whether the story is read-only). */
export function setStoryboardCues(view: EditorView, cues: SlotCue[], readOnly: boolean): void {
  if (!storyboardKey.getState(view.state)) return;
  view.dispatch(view.state.tr.setMeta(storyboardKey, { cues, readOnly }));
}

export function currentSlots(state: EditorState): CueSlot[] {
  return storyboardKey.getState(state)?.slots ?? [];
}

export const sameSlots = (a: CueSlot[], b: CueSlot[]) => a.length === b.length && a.every((s, i) => s.key === b[i]!.key && s.pos === b[i]!.pos && s.following.join('\n') === b[i]!.following.join('\n'));
